import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import axios from 'axios';
import { FiLoader } from 'react-icons/fi';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import ConfirmModalB from '../../common/ConfirmModalB'; 
// Définition du type des réponses
interface Answer {
  id?: string;  // id de la réponse, peut être défini pour les réponses existantes
  _id?: number; // _id de la réponse, peut être défini pour certaines réponses
  name: string;  // Nom de la réponse
  order?: number; // Ordre de la réponse
}

interface AddQuestionFormData {
  name: string;
  order: number;
  category_id: number | string;
  answers: Answer[]; // Tableau de réponses
}

interface AddQuestionProps {
  questionId?: number; // Id de la question à modifier
  setActiveView: React.Dispatch<React.SetStateAction<'list' | 'add' | 'edit'>>; // Fonction pour changer la vue
}

const AddQuestion: React.FC<AddQuestionProps> = ({ questionId, setActiveView }) => {
  const { register, handleSubmit, control, reset } = useForm<AddQuestionFormData>({
    defaultValues: {
      answers: [] // Définir une valeur par défaut pour le tableau de réponses
    }
  });

  const { fields: answerFields, append: appendQuestion, remove: removeQuestion } = useFieldArray({
    control,
    name: 'answers',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [categories, setCategories] = useState<any[]>([]); // State pour stocker les catégories disponibles
  const [selectedCategory, setSelectedCategory] = useState<number | string>(''); // Pour stocker la catégorie sélectionnée
   const [isModalOpen, setIsModalOpen] = useState(false); // Control modal state
  const [confirmationData, setConfirmationData] = useState<any>(null); // Store the confirmation data
  const [existingAnswers, setExistingAnswers] = useState<string[]>([]); // Stocker les réponses existantes

useEffect(() => {
  if (questionId) {
    const fetchQuestionData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/questions_show/${questionId}`);
        const data = response.data.question;
        reset({
          name: data.text || '',
          order: data.order || '',
          category_id: data.category_id || '', // Ajouter la catégorie ici
          answers: data.answers?.map((answer: any) => ({
            id: answer.id || '',
            _id: answer.id || '', // Charger _id si disponible
            name: answer.testo || '',
            order: answer.order || 0,
          })) || [],
        });

        // Stocker les réponses existantes dans un tableau
        const existingAnswerNames = data.answers?.map((answer: any) => answer.testo) || [];
        setExistingAnswers(existingAnswerNames);

        setSelectedCategory(data.category_id); // Mettre à jour la catégorie sélectionnée

      } catch (error) {
        console.error('Errore durante il caricamento dei dati', error);
        setMessage({ type: 'error', text: 'Errore durante il caricamento dei dati' });
      }
    };

    fetchQuestionData();
  }
}, [questionId, reset]);

  // Effet pour charger les données existantes si l'on est en mode modification
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories`);
        setCategories(response.data); // Remplir les catégories disponibles
      } catch (error) {
        console.error('Errore durante il caricamento delle categorie', error);
      }
    };

    fetchCategories();

    if (questionId) {
      const fetchQuestionData = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/questions_show/${questionId}`);
          const data = response.data.question;
          reset({
            name: data.text || '',
            order: data.order || '',
            category_id: data.category_id || '', // Ajouter la catégorie ici
            answers: data.answers?.map((answer: any) => ({
              id: answer.id || '',
              _id: answer.id || '', // Charger _id si disponible
              name: answer.testo || '',
              order: answer.order || 0,
            })) || [],
          });

          setSelectedCategory(data.category_id); // Mettre à jour la catégorie sélectionnée

        } catch (error) {
          console.error('Errore durante il caricamento dei dati', error);
          setMessage({ type: 'error', text: 'Errore durante il caricamento dei dati' });
        }
      };

      fetchQuestionData();
    }
  }, [questionId, reset]);

  // Fonction de soumission du formulaire
  const onSubmit = async (data: any) => {
    // Vérification des réponses existantes
    const newAnswers = data.answers.filter((answer: any) => !answer.id); // Filtrer les nouvelles réponses (sans id)
  
    // Vérifier les doublons entre les nouvelles réponses et celles déjà existantes
    const duplicateAnswers = newAnswers.filter((answer: any) =>
      existingAnswers.includes(answer.name) // Vérifier si la réponse existe déjà parmi les réponses existantes
    );
  
    // console.log('Réponses en doublon :', duplicateAnswers);
  
    if (duplicateAnswers.length > 0) {
      setMessage({
        type: 'error',
        text: 'Alcune risposte sono già presenti per questa domanda.',
      });
      setTimeout(() => setActiveView('list'), 2000);
      return; // Arrêter le processus de soumission si des doublons sont trouvés
    }
  
    // Séparer les réponses existantes et nouvelles
    const updatedAnswers = data.answers
      .filter((answer: any) => answer.id)  // Réponses avec ID (existant)
      .map((answer: any) => ({
        id: answer.id, // Conserver l'ID pour la mise à jour
        _id: answer.id, // Conserver _id si disponible
        testo: answer.name, // Nouveau texte
        order: answer.order, // Ordre
      }));
  
    const newAnswersToAdd = data.answers
      .filter((answer: any) => !answer.id)  // Réponses sans ID (nouvelle)
      .map((answer: any) => ({
        testo: answer.name, // Nouveau texte
        order: answer.order, // Ordre
      }));
  
    // Données à envoyer au backend
    const questionData = {
      text: data.name, // Texte de la question
      order: data.order, // Ordre de la question
      category_id: data.category_id, // Catégorie
      answers: [...updatedAnswers, ...newAnswersToAdd], // Réponses mises à jour et nouvelles
    };

    try {
      setIsSubmitting(true);
  
      if (questionId) {
        // Mode modification : envoyer directement une mise à jour
        const updateResponse = await axios.put(`${process.env.REACT_APP_API_URL}/questions/${questionId}`, questionData);
        setMessage({
          type: 'success',
          text: 'La domanda è stata modificata con successo.',
        });
        setIsModalOpen(false);
        setTimeout(() => setActiveView('list'), 2000);
      } else {
        // Mode ajout : vérifier si la question existe déjà
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/questions/search`, {
          params: { text: data.name },
        });
  
        if (response.data.exists) {
          // Si la question existe déjà, afficher une alerte de confirmation
          setConfirmationData({
            title: "domanda esistente",
            message: "Questa domanda esiste già. Vuoi aggiungere queste risposte alla domanda esistente?",
            onConfirm: async () => {
              const existingQuestionId = response.data.question.id;
              await axios.put(`${process.env.REACT_APP_API_URL}/questions/${existingQuestionId}`, questionData);
              setMessage({
                type: 'success',
                text: 'Le risposte sono state aggiunte con successo alla domanda esistente.',
              });
              setIsModalOpen(false);
              setTimeout(() => setActiveView('list'), 2000);
            },
          });
          setIsModalOpen(true);
        } else {
          // Si la question n'existe pas, envoyer une nouvelle question
          const createResponse = await axios.post(`${process.env.REACT_APP_API_URL}/questions`, questionData);
          setMessage({
            type: 'success',
            text: 'La domanda è stata aggiunta con successo.',
          });
          setIsModalOpen(false);
          setTimeout(() => setActiveView('list'), 2000);
        }
      }
    } catch (error: any) {
      console.error('Errore durante l\'invio.', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Errore durante il salvataggio.',
      });
      setTimeout(() => setActiveView('list'), 2000);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  
  // Fonction pour supprimer une réponse
  const handleDeleteAnswer = async (index: number) => {
    const answerData = answerFields[index];

    // Si _id est défini, on l'utilise, sinon on utilise id
    const answerId = answerData._id || answerData.id;

    if (questionId && answerId) {
      try {
        // Envoyer une requête DELETE à l'API pour supprimer la relation
        await axios.delete(`${process.env.REACT_APP_API_URL}/question/${questionId}/answer/${answerId}`);
        console.log(`Risposta con _id ${answerId} eliminata con successo.`);
      } catch (error) {
        console.error('Errore durante l\'eliminazione della risposta', error);
        setMessage({ type: 'error', text: 'Errore durante l\'eliminazione della risposta' });
      }
    }

    // Supprimer la réponse du formulaire localement
    removeQuestion(index);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200/80 transition-all duration-300 hover:shadow-md">
      <div className="px-5 py-4 border-b bg-gradient-to-r from-gray-50 to-gray-100/50">
        <h2 className="font-semibold text-gray-700">{questionId ? 'Modifica una domanda' : 'Aggiungi una domanda'}</h2>
      </div>

      <div className="p-5">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Message d'alerte */}
          {message && (
            <div
              className={`text-sm p-3 rounded ${message.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-100 text-green-700'} flex justify-between items-center`}
            >
              {message.text}
              <button onClick={() => setMessage(null)} className="ml-2">
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Sélection de la catégorie */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
            <select
              {...register('category_id', { required: true })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              value={selectedCategory} // Met à jour la catégorie sélectionnée
              onChange={(e) => setSelectedCategory(Number(e.target.value))}
            >
              <option value="">Seleziona una categoria</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          {/* Question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Domanda</label>
            <input
              {...register('name', { required: true })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
              required
            />
          </div>

          {/* Ordre de la question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
            <input
              type="number"
              {...register('order')}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
            />
          </div>

          {/* Réponses */}
          <div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Risposte</span>
              <button
                type="button"
                onClick={() => appendQuestion({ name: '', order: 0 })}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg text-sm hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center gap-2"
              >
                <FaPlus /> Aggiungi una risposta
              </button>
            </div>

            <div className="mt-3 space-y-6">
              {answerFields.map((answer, qIndex) => (
                <div key={answer.id} className="border p-4 rounded-lg shadow-sm bg-gray-50">
                  <div className="flex gap-4 items-center">
                    <input
                      {...register(`answers.${qIndex}.name`, { required: true })}
                      disabled={!!answer._id}
                      placeholder="Nome della risposta"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                      required
                    />
                    <input
                      {...register(`answers.${qIndex}.order`)}
                      disabled={!!answer._id}
                      type="number"
                      placeholder="Ordine"
                      className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteAnswer(qIndex)}
                      className="text-red-500 hover:text-red-700 transition-all"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg text-sm
              hover:from-indigo-600 hover:to-purple-600 transition-all flex items-center justify-center gap-2
              disabled:opacity-70 disabled:cursor-not-allowed shadow-sm hover:shadow"
          >
            {isSubmitting && <FiLoader className="w-4 h-4 animate-spin" />}
            {isSubmitting ? 'Salvataggio...' : questionId ? 'Modifica' : 'Aggiungi'}
          </button>
        </form>
      </div>
      {isModalOpen && confirmationData && (
        <ConfirmModalB
          isOpen={isModalOpen}
          title={confirmationData.title}
          message={confirmationData.message}
          onConfirm={confirmationData.onConfirm}
          onCancel={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AddQuestion;
